import React, { Fragment, useState } from "react";

const T = {
  bg: "#000000",
  surface: "#0A0A0A",
  surfaceRaised: "#111111",
  surfaceHover: "#1A1A1A",
  border: "#1C1C1E",
  text: "#FFFFFF",
  textSecondary: "#8E8E93",
  textTertiary: "#48484A",
  accent: "#007AFF",
  accentDim: "rgba(0,122,255,0.12)",
  green: "#30D158",
  greenDim: "rgba(48,209,88,0.12)",
  red: "#FF453A",
  redDim: "rgba(255,69,58,0.12)",
  orange: "#FF9F0A",
  orangeDim: "rgba(255,159,10,0.12)",
  purple: "#BF5AF2",
  purpleDim: "rgba(191,90,242,0.12)",
  cyan: "#64D2FF",
  cyanDim: "rgba(100,210,255,0.12)",
};

const ACCENT = T.accent;
const mono = "'SF Mono','Fira Code','Cascadia Code','Consolas',monospace";
const sans = "-apple-system,BlinkMacSystemFont,'SF Pro Display','Segoe UI',system-ui,sans-serif";

const css = `
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
@keyframes growBar{from{width:0}}
@keyframes scaleIn{from{opacity:0;transform:scale(.86)}to{opacity:1;transform:scale(1)}}
@keyframes dashDraw{from{stroke-dashoffset:600}to{stroke-dashoffset:0}}
*{box-sizing:border-box}
::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:${T.border};border-radius:4px}
.tab-btn:hover{background:${T.surfaceHover}!important}
.node-card{transition:border-color .2s,transform .2s,box-shadow .2s}
.node-card:hover{border-color:${T.accent}55!important;transform:translateY(-3px);box-shadow:0 8px 24px rgba(0,122,255,.08)}
.link-card{transition:border-color .2s,transform .15s,box-shadow .2s}
.link-card:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(0,0,0,.3)}
.bar-row{transition:transform .15s}
.bar-row:hover{transform:translateX(2px)}
`;

const formatNumber = (value) => {
  if (typeof value !== "number") return String(value);
  return value < 1 ? value.toFixed(3) : value.toFixed(2);
};

const SectionLabel = ({ children, delay = 0 }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: ".12em",
      textTransform: "uppercase",
      color: T.textSecondary,
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 8,
      animation: `fadeUp .4s ease ${delay}s both`,
    }}
  >
    <span style={{ width: 3, height: 12, borderRadius: 2, background: T.accent, display: "inline-block" }} />
    {children}
  </div>
);

const Tag = ({ children, color = T.accent, bg = T.accentDim }) => (
  <span
    style={{
      display: "inline-flex",
      padding: "3px 10px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      fontFamily: mono,
      color,
      background: bg,
      border: `1px solid ${color}22`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const MetricCard = ({ label, value, color = T.accent, delay = 0 }) => (
  <div
    style={{
      flex: "1 1 0",
      minWidth: 94,
      padding: "16px 12px",
      borderRadius: 12,
      background: T.surfaceRaised,
      border: `1px solid ${T.border}`,
      textAlign: "center",
      animation: `fadeUp .45s ease ${delay}s both`,
    }}
  >
    <div style={{ fontSize: 9, color: T.textSecondary, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 8 }}>
      {label}
    </div>
    <div style={{ fontSize: 23, fontWeight: 800, color, fontFamily: mono, lineHeight: 1 }}>
      {formatNumber(value)}
    </div>
  </div>
);

export const ArchitecturePipeline = ({ nodes = [] }) => (
  <div style={{ overflowX: "auto", paddingBottom: 8 }}>
    <div style={{ display: "flex", alignItems: "center", minWidth: "max-content" }}>
      {nodes.map((node, i) => (
        <Fragment key={`${node}-${i}`}>
          <div
            className="node-card"
            style={{
              minWidth: 104,
              padding: "13px 10px",
              borderRadius: 12,
              background: T.surfaceRaised,
              border: `1px solid ${T.border}`,
              textAlign: "center",
              animation: `scaleIn .35s ease ${i * 0.06}s both`,
            }}
          >
            <div style={{ fontSize: 10, color: T.textTertiary, fontFamily: mono, marginBottom: 5 }}>
              step {i + 1}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.text, lineHeight: 1.25 }}>
              {node}
            </div>
          </div>
          {i < nodes.length - 1 && (
            <svg width="24" height="12" viewBox="0 0 24 12" style={{ flexShrink: 0, animation: `fadeIn .3s ease ${i * 0.06 + 0.1}s both` }}>
              <line x1="0" y1="6" x2="16" y2="6" stroke={T.textTertiary} strokeWidth="1.2" />
              <path d="M14 2.5l5.5 3.5-5.5 3.5" fill="none" stroke={T.accent} strokeWidth="1.3" strokeOpacity=".6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </Fragment>
      ))}
    </div>
  </div>
);

export const MetricGrid = ({ metrics = {} }) => {
  const keys = ["accuracy", "r2", "rmse", "mae", "precision", "recall", "f1"];
  const labels = {
    accuracy: "Accuracy",
    r2: "R2",
    rmse: "RMSE",
    mae: "MAE",
    precision: "Precision",
    recall: "Recall",
    f1: "F1",
  };
  const colors = [T.accent, T.green, T.orange, T.purple, T.cyan, T.red, T.accent];
  const avail = keys.filter((key) => typeof metrics[key] === "number");
  if (!avail.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {avail.map((key, index) => (
        <MetricCard key={key} label={labels[key]} value={metrics[key]} color={colors[index]} delay={index * 0.04} />
      ))}
    </div>
  );
};

export const LossChart = ({ trainingLoss, validationLoss, trainingAccuracy, validationAccuracy, mode = "loss" }) => {
  const train = mode === "loss" ? trainingLoss : trainingAccuracy;
  const val = mode === "loss" ? validationLoss : validationAccuracy;
  const [hover, setHover] = useState(null);
  if (!train?.length) return null;

  const data = train.map((v, i) => ({ epoch: i + 1, train: v, val: val?.[i] }));
  const values = data.flatMap((d) => [d.train, d.val].filter((v) => typeof v === "number"));
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const W = 540;
  const H = 230;
  const pad = { t: 24, r: 20, b: 34, l: 46 };
  const w = W - pad.l - pad.r;
  const h = H - pad.t - pad.b;
  const x = (i) => pad.l + (i / Math.max(1, data.length - 1)) * w;
  const y = (v) => pad.t + (1 - (v - min) / range) * h;
  const path = (key) => data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(" ");

  return (
    <div style={{ background: T.surfaceRaised, borderRadius: 14, border: `1px solid ${T.border}`, padding: "14px 10px 6px", animation: "fadeUp .4s ease both" }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id={`lossFill-${mode}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity=".18" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const gy = pad.t + frac * h;
          const gv = max - frac * range;
          return (
            <g key={frac}>
              <line x1={pad.l} y1={gy} x2={W - pad.r} y2={gy} stroke={T.border} strokeWidth=".5" />
              <text x={pad.l - 6} y={gy + 3} textAnchor="end" fill={T.textTertiary} fontSize="8.5" fontFamily={mono}>
                {gv.toFixed(2)}
              </text>
            </g>
          );
        })}
        <path d={`${path("train")} L${x(data.length - 1)},${y(min)} L${x(0)},${y(min)} Z`} fill={`url(#lossFill-${mode})`} />
        <path d={path("train")} fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ strokeDasharray: 600, animation: "dashDraw 1.2s ease both" }} />
        {val && <path d={path("val")} fill="none" stroke={T.orange} strokeWidth="2" strokeDasharray="5 3" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "fadeIn .8s ease .4s both" }} />}
        {data.map((d, i) => (
          <g key={d.epoch} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: "crosshair" }}>
            <rect x={x(i) - 16} y={pad.t - 5} width="32" height={h + 10} fill="transparent" />
            {hover === i && (
              <>
                <line x1={x(i)} y1={pad.t} x2={x(i)} y2={pad.t + h} stroke={T.textTertiary} strokeWidth=".5" strokeDasharray="3 2" />
                <circle cx={x(i)} cy={y(d.train)} r="4.5" fill={T.accent} stroke={T.bg} strokeWidth="2" />
                {typeof d.val === "number" && <circle cx={x(i)} cy={y(d.val)} r="4.5" fill={T.orange} stroke={T.bg} strokeWidth="2" />}
                <rect x={Math.max(8, Math.min(W - 138, x(i) - 65))} y={Math.max(8, y(d.train) - 44)} width="130" height="34" rx="7" fill={T.surfaceRaised} stroke={T.border} />
                <text x={Math.max(73, Math.min(W - 73, x(i)))} y={Math.max(28, y(d.train) - 23)} textAnchor="middle" fill={T.text} fontSize="9.5" fontFamily={mono} fontWeight="600">
                  {`E${d.epoch} T:${d.train.toFixed(3)}${typeof d.val === "number" ? ` V:${d.val.toFixed(3)}` : ""}`}
                </text>
              </>
            )}
          </g>
        ))}
        <g transform={`translate(${pad.l + 6},${pad.t + 2})`}>
          <line x1="0" y1="0" x2="14" y2="0" stroke={T.accent} strokeWidth="2" />
          <text x="18" y="3.5" fill={T.textSecondary} fontSize="9" fontFamily={mono}>train</text>
          {val && (
            <>
              <line x1="56" y1="0" x2="70" y2="0" stroke={T.orange} strokeWidth="2" strokeDasharray="4 3" />
              <text x="74" y="3.5" fill={T.textSecondary} fontSize="9" fontFamily={mono}>val</text>
            </>
          )}
        </g>
      </svg>
    </div>
  );
};

export const ConfusionMatrix = ({ matrix, labels = [] }) => {
  if (!matrix?.length) return null;
  const max = Math.max(...matrix.flat());
  const displayLabels = labels.length ? labels : matrix.map((_, i) => `Class ${i + 1}`);
  const shade = (value) => {
    const ratio = value / max;
    if (ratio > 0.5) return { bg: T.accent, color: "#fff" };
    if (ratio > 0.1) return { bg: T.accentDim, color: T.accent };
    return { bg: T.redDim, color: T.red };
  };

  return (
    <div style={{ display: "inline-block", animation: "fadeUp .4s ease both" }}>
      <div style={{ display: "grid", gridTemplateColumns: `80px repeat(${matrix.length}, 72px)`, gap: 5, alignItems: "center" }}>
        <div />
        {displayLabels.map((label, i) => (
          <div key={i} style={{ fontSize: 9, color: T.textSecondary, textAlign: "center", fontWeight: 600, fontFamily: mono }}>
            {label}
          </div>
        ))}
        {matrix.map((row, rowIndex) => (
          <Fragment key={rowIndex}>
            <div style={{ fontSize: 9, color: T.textSecondary, fontWeight: 600, fontFamily: mono, textAlign: "right", paddingRight: 7 }}>
              {displayLabels[rowIndex]}
            </div>
            {row.map((value, colIndex) => {
              const c = shade(value);
              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{
                    width: 72,
                    height: 58,
                    borderRadius: 10,
                    background: c.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: c.color,
                    fontSize: 13,
                    fontWeight: 800,
                    fontFamily: mono,
                    border: `1px solid ${T.border}`,
                    animation: `scaleIn .35s ease ${(rowIndex * row.length + colIndex) * 0.06}s both`,
                  }}
                >
                  {value}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export const FeatureImportance = ({ features = [] }) => {
  if (!features.length) return null;
  const sorted = [...features].sort((a, b) => (b.value || 0) - (a.value || 0));
  const max = sorted[0].value || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {sorted.map((feature, i) => {
        const pct = ((feature.value || 0) / max) * 100;
        return (
          <div key={feature.name} className="bar-row" style={{ display: "flex", alignItems: "center", gap: 8, animation: `slideIn .3s ease ${i * 0.03}s both` }}>
            <div style={{ width: 112, fontSize: 10, fontFamily: mono, color: T.textSecondary, textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {feature.name}
            </div>
            <div style={{ flex: 1, height: 16, background: T.surfaceRaised, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: `linear-gradient(90deg,${T.accent}cc,${T.cyan}66)`, animation: `growBar .5s ease ${i * 0.04 + 0.15}s both` }} />
            </div>
            <div style={{ fontSize: 10, color: T.textTertiary, fontFamily: mono, width: 42, textAlign: "right" }}>
              {formatNumber(feature.value)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ReproduceSection = ({ notebookUrl, datasetUrl, reportUrl }) => {
  const items = [
    { label: "Notebook", url: notebookUrl, color: T.orange },
    { label: "Dataset", url: datasetUrl, color: T.green },
    { label: "Report", url: reportUrl, color: T.accent },
  ].filter((item) => item.url);

  if (!items.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map((item, i) => (
        <a
          key={item.label}
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="link-card"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 18px",
            borderRadius: 10,
            background: T.surfaceRaised,
            border: `1px solid ${T.border}`,
            color: item.color,
            fontSize: 12,
            fontWeight: 600,
            textDecoration: "none",
            cursor: "pointer",
            fontFamily: sans,
            animation: `fadeUp .35s ease ${i * 0.08}s both`,
          }}
        >
          {item.label}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.4 }}>
            <path d="M5 1H2.5A1.5 1.5 0 001 2.5v7A1.5 1.5 0 002.5 11h7A1.5 1.5 0 0011 9.5V7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M7 1h4v4M11 1L5.5 6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      ))}
    </div>
  );
};

const TABS = ["Overview", "Training", "Evaluation", "Reproduce"];

const TabBar = ({ active, onChange }) => (
  <div style={{ display: "flex", gap: 2, padding: 3, borderRadius: 10, background: T.surfaceRaised, border: `1px solid ${T.border}`, width: "fit-content", maxWidth: "100%", overflowX: "auto" }}>
    {TABS.map((tab) => (
      <button
        key={tab}
        className="tab-btn"
        onClick={() => onChange(tab)}
        style={{
          padding: "7px 16px",
          borderRadius: 8,
          border: "none",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          background: active === tab ? T.accent : "transparent",
          color: active === tab ? "#fff" : T.textSecondary,
          fontFamily: sans,
          transition: "all .2s",
          whiteSpace: "nowrap",
        }}
      >
        {tab}
      </button>
    ))}
  </div>
);

export function MLModelView({ project }) {
  const [tab, setTab] = useState("Overview");
  const [curveMode, setCurveMode] = useState("loss");

  if (!project) return null;

  const {
    title,
    summary,
    tags = [],
    architecture = [],
    hyperparameters = {},
    metrics = {},
    datasetStats,
    notebookUrl,
    datasetUrl,
    reportUrl,
    notes = [],
  } = project;
  const hasCurves = metrics.trainingLoss || metrics.trainingAccuracy;
  const hasEvaluation = metrics.confusionMatrix || metrics.featureImportance;
  const hasLinks = notebookUrl || datasetUrl || reportUrl;

  return (
    <div style={{ width: "100%", minHeight: "100%", background: T.bg, color: T.text, fontFamily: sans, WebkitFontSmoothing: "antialiased" }}>
      <style>{css}</style>
      <div style={{ maxWidth: 896, margin: "0 auto", padding: "28px 24px 0" }}>
        <div style={{ animation: "fadeUp .4s ease both" }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.025em", margin: "0 0 4px", lineHeight: 1.2 }}>{title}</h1>
          {summary && <p style={{ fontSize: 13, color: T.textSecondary, margin: "0 0 14px", lineHeight: 1.7, maxWidth: 660 }}>{summary}</p>}
        </div>
        {tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20, animation: "fadeUp .4s ease .05s both" }}>
            {tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
          </div>
        )}
        <div style={{ animation: "fadeUp .4s ease .1s both" }}>
          <TabBar active={tab} onChange={setTab} />
        </div>
      </div>

      <div key={tab} style={{ maxWidth: 896, margin: "0 auto", padding: 24 }}>
        {tab === "Overview" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            {architecture.length > 0 && (
              <div>
                <SectionLabel>Architecture</SectionLabel>
                <ArchitecturePipeline nodes={architecture} />
              </div>
            )}
            {Object.keys(hyperparameters).length > 0 && (
              <div>
                <SectionLabel delay={0.08}>Hyperparameters</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.entries(hyperparameters).map(([key, value], i) => (
                    <div key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 8, background: T.surfaceRaised, border: `1px solid ${T.border}`, fontSize: 11, fontFamily: mono, animation: `fadeIn .3s ease ${i * 0.03}s both` }}>
                      <span style={{ color: T.textSecondary }}>{key}</span>
                      <span style={{ color: T.textTertiary }}>:</span>
                      <span style={{ color: T.orange, fontWeight: 600 }}>{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <SectionLabel delay={0.14}>Metrics</SectionLabel>
              <MetricGrid metrics={metrics} />
            </div>
          </div>
        )}

        {tab === "Training" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            {hasCurves ? (
              <div>
                <SectionLabel>Training Curves</SectionLabel>
                <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                  {["loss", "accuracy"].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setCurveMode(mode)}
                      style={{
                        border: `1px solid ${curveMode === mode ? T.accent : T.border}`,
                        background: curveMode === mode ? T.accentDim : T.surfaceRaised,
                        color: curveMode === mode ? T.accent : T.textSecondary,
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".08em",
                        textTransform: "uppercase",
                        cursor: "pointer",
                      }}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                <LossChart {...metrics} mode={curveMode} />
              </div>
            ) : (
              <div style={{ color: T.textTertiary, fontSize: 13 }}>No training curve data available.</div>
            )}
            {Object.keys(hyperparameters).length > 0 && (
              <div>
                <SectionLabel delay={0.1}>Training Configuration</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.entries(hyperparameters).map(([key, value]) => <Tag key={key} color={T.textSecondary} bg={T.surfaceRaised}>{`${key}: ${value}`}</Tag>)}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "Evaluation" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            <div>
              <SectionLabel>Metrics</SectionLabel>
              <MetricGrid metrics={metrics} />
            </div>
            {hasEvaluation ? (
              <>
                {metrics.confusionMatrix && (
                  <div>
                    <SectionLabel delay={0.08}>Confusion Matrix</SectionLabel>
                    <ConfusionMatrix matrix={metrics.confusionMatrix} labels={metrics.classLabels} />
                  </div>
                )}
                {metrics.featureImportance && (
                  <div>
                    <SectionLabel delay={0.14}>Feature Importance</SectionLabel>
                    <FeatureImportance features={metrics.featureImportance} />
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: T.textTertiary, fontSize: 13 }}>No evaluation detail data available.</div>
            )}
          </div>
        )}

        {tab === "Reproduce" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            {hasLinks && (
              <div>
                <SectionLabel>Reproduce</SectionLabel>
                <ReproduceSection notebookUrl={notebookUrl} datasetUrl={datasetUrl} reportUrl={reportUrl} />
              </div>
            )}
            {datasetStats && (
              <div>
                <SectionLabel delay={0.08}>Dataset</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {Object.entries(datasetStats).map(([key, value], i) => (
                    <MetricCard key={key} label={key} value={value} color={[T.text, T.cyan, T.green, T.orange][i % 4]} delay={i * 0.04} />
                  ))}
                </div>
              </div>
            )}
            {notes.length > 0 && (
              <div>
                <SectionLabel delay={0.14}>Engineer Notes</SectionLabel>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                  {notes.map((note, i) => (
                    <li key={i} style={{ color: T.textSecondary, fontSize: 12, lineHeight: 1.6, background: T.surfaceRaised, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", animation: `fadeUp .35s ease ${i * 0.05}s both` }}>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!hasLinks && !datasetStats && !notes.length && (
              <div style={{ color: T.textTertiary, fontSize: 13 }}>No reproduce, dataset, or notes content available.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const DEMO_PROJECT = {
  id: 1,
  title: "Appliance Energy Usage Forecasting",
  summary: "ExtraTrees regressor on IoT sensor telemetry from a Belgian residence. RFE-selected top-5 features; NSM and atmospheric pressure dominate.",
  architecture: ["Raw CSV", "Feature Eng", "RFE Top-5", "ExtraTrees", "GridSearchCV", "Predictions"],
  hyperparameters: { n_estimators: 500, max_depth: 30, min_samples_split: 2, min_samples_leaf: 1, random_state: 42 },
  metrics: {
    r2: 0.4832,
    rmse: 0.7189,
    trainingLoss: [0.82, 0.65, 0.51, 0.42, 0.35, 0.28],
    validationLoss: [0.88, 0.75, 0.68, 0.62, 0.58, 0.55],
    trainingAccuracy: [0.61, 0.68, 0.74, 0.81, 0.88, 0.94],
    validationAccuracy: [0.58, 0.64, 0.71, 0.76, 0.82, 0.89],
    confusionMatrix: [[45, 5], [3, 47]],
    classLabels: ["Low", "High"],
    featureImportance: [
      { name: "NSM", value: 0.30 },
      { name: "Press_mm_hg", value: 0.23 },
      { name: "T3", value: 0.22 },
      { name: "RH_5", value: 0.20 },
      { name: "lights", value: 0.05 },
    ],
  },
  datasetStats: { rows: 19735, features: 29, target: "Appliances" },
  tags: ["Python", "scikit-learn", "XGBoost", "Time Series"],
  notebookUrl: "https://github.com",
  reportUrl: "https://github.com",
  datasetUrl: "https://archive.ics.uci.edu/ml/datasets/Appliances+energy+prediction",
  notes: [
    "Top 5 engineered features dominated model performance via RFE selection.",
    "Temporal signal was strongest through NSM and hourly structure.",
    "ExtraTrees achieved R2=0.4832 after GridSearchCV hyperparameter tuning.",
  ],
};

export default function App() {
  return <MLModelView project={DEMO_PROJECT} />;
}